import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { TodoService } from "services";
import { AppUtils, UserUtils } from "utils";

import { createTodoGroupLabels, groupTodosByDate } from "../../todo/list/helpers";
import { TodoItemData } from "../../todo/list/item";
import TodoGroupedList from "../../todo/list/TodoGroupedList";

const { REQUEST_TODO_LIST_ID } = ServiceConstants;

const DASHBOARD_TODO_PAGE_SIZE = 20;

interface DashboardTodoContentProps {
  height?: string | number;
  contentInsetX?: string;
}

const DashboardTodoContent = ({
  height = "100%",
  contentInsetX = "var(--mantine-spacing-xl)",
}: DashboardTodoContentProps) => {
  const { t } = useTranslation();
  const canEdit = UserUtils.hasAuthority(AuthorityConstants.ROLE_TODO_CUD);
  const todoListService = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TODO_LIST_ID],
  );

  const [todos, setTodos] = useState<TodoItemData[]>([]);
  const [loading, setLoading] = useState(true);
  const todoGroupLabels = useMemo(() => createTodoGroupLabels(t), [t]);

  const loadTodos = useCallback(() => {
    setLoading(true);

    TodoService.todoList({
      myTodo: true,
      archive: false,
      dateFilter: "NEXT_7_DAYS",
      page: 0,
      pageSize: DASHBOARD_TODO_PAGE_SIZE,
    });
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      todoListService,
      REQUEST_TODO_LIST_ID,
      (data) => {
        const list = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
            ? data
            : [];

        setTodos(list.slice(0, DASHBOARD_TODO_PAGE_SIZE));
        setLoading(false);
      },
      () => {
        setTodos([]);
        setLoading(false);
      },
    );
  }, [todoListService]);

  const groupedTodos = useMemo(
    () => groupTodosByDate(todos, todoGroupLabels),
    [todos, todoGroupLabels],
  );

  const handleCompleteTodo = useCallback(
    async (id: number) => {
      await TodoService.todoMarkCompleted(id);
      loadTodos();
    },
    [loadTodos],
  );

  return (
    <TodoGroupedList
      groupedTodos={groupedTodos}
      loading={loading}
      emptyMessage={t("dashboard.todo.empty")}
      height={height}
      contentInsetX={contentInsetX}
      showCheckbox={canEdit}
      onCompleteTodo={canEdit ? handleCompleteTodo : undefined}
    />
  );
};

export default DashboardTodoContent;
